import React, { useState, useEffect, useCallback } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";

import alertModalStyles from "../../../../Reusables/components/AlertModal/AlertModal.module.css";
import styles from "./IntakeFormAIModal.module.css";

import CheckboxInput from "../../../../Reusables/components/CheckboxInput/CheckboxInput";
import TextInput from "../../../../Reusables/components/TextInput/TextInput";
import SelectInput from "../../../../Reusables/components/SelectInput/SelectInput";
import DropFileInput from "../../../../pages/reusable_components/drop_file_component/DropFileComponent";
import CustomButton from "../../../../pages/reusable_components/custom_button_component/Button";

import {
  IntakeFormAIModalProps,
  SelectedField,
  FormConfig,
  TYPE_ICON_MAP,
  FORM_TYPE_OPTIONS,
  EMPTY_CONFIG,
} from "./IntakeFormAIModal.types";
import {
  fetchIntakeFormFields,
  fetchDefaultIntakeFields,
  generateIntakeForm,
  modifyForm,
  saveDefaultIntakeFields,
} from "./IntakeFormAIModal.service";
import {
  filterFieldsByQuery,
  areAllSelected,
  isFormConfigValid,
  mapToCascadingOptions,
  groupFieldsByCategory,
} from "./IntakeFormAIModal.utils";

import { useGlobalStore } from "../../../store/globalStore";

const ACCEPTED_ATTACHMENT_MIME = new Set([
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

const IntakeFormAIModal: React.FC<IntakeFormAIModalProps> = ({
  show,
  onHide,
  onDone,
  mode = "generate",
  showFieldPicker = true,
  initialConfig,
  formId,
  templateId,
  formType,
  currentFieldCodes,
}) => {
  const [fields, setFields] = useState<SelectedField[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [formConfig, setFormConfig] = useState<FormConfig>(EMPTY_CONFIG);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSavingDefault, setIsSavingDefault] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const lineOfServiceOptions = mapToCascadingOptions(useGlobalStore((s) => s.purposeOptions));

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (mode === "modify" && currentFieldCodes) {
        const uniqueFields = await fetchIntakeFormFields();
        setFields(uniqueFields);
        const codeSet = new Set(currentFieldCodes);
        setSelectedIds(new Set(uniqueFields.filter((f) => codeSet.has(f.code)).map((f) => f.id)));
      } else {
        const [uniqueFields, defaultCodes] = await Promise.all([
          fetchIntakeFormFields(),
          fetchDefaultIntakeFields(),
        ]);
        setFields(uniqueFields);
        if (defaultCodes && defaultCodes.length > 0) {
          const codeSet = new Set(defaultCodes);
          setSelectedIds(new Set(uniqueFields.filter((f) => codeSet.has(f.code)).map((f) => f.id)));
        }
      }
    } catch (err) {
      console.error("Failed to fetch modal data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [mode, currentFieldCodes]);

  useEffect(() => {
    if (show) {
      setSelectedIds(new Set());
      setFormConfig({ ...EMPTY_CONFIG, ...(initialConfig ?? {}) });
      setAttachment(null);
      setSearchQuery("");
      setCollapsedSections(new Set());
      if (showFieldPicker !== false) {
        fetchData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, fetchData, showFieldPicker]);

  const toggleField = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(filteredFields.map((f) => f.id)) : new Set());
  };

  const filteredFields = filterFieldsByQuery(fields, searchQuery);
  const allFilteredSelected = areAllSelected(filteredFields, selectedIds);

  const canSubmit =
    mode === "generate"
      ? isFormConfigValid(formConfig, selectedIds, !!attachment)
      : showFieldPicker
      ? formConfig.instructions.trim().length > 0 || selectedIds.size > 0
      : formConfig.instructions.trim().length > 0;

  const handleSetAsDefault = async () => {
    const selectedFields = fields.filter((f) => selectedIds.has(f.id));
    setIsSavingDefault(true);
    try {
      await saveDefaultIntakeFields(selectedFields.map((f) => f.code));
    } finally {
      setIsSavingDefault(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const selectedFields = fields.filter((f) => selectedIds.has(f.id));
    setIsSubmitting(true);
    try {
      if (mode === "modify") {
        const resolvedFormType = formType ?? "Intake";
        await modifyForm(formId!, templateId!, selectedFields, formConfig.instructions, resolvedFormType);
      } else {
        await generateIntakeForm(formConfig, selectedFields, attachment);
      }
      onHide();
      onDone?.();
    } catch (err) {
      console.error("Error submitting form:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = mode === "modify" ? "Modify Form with AI" : "Generate Intake Form";
  const subtitle =
    mode === "modify"
      ? "Add new fields or describe your changes via instructions"
      : "Select the client fields to include in the AI-generated intake form";
  const submitLabel = mode === "modify" ? "Modify with AI" : "Generate with AI";

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      keyboard={false}
      dialogClassName={alertModalStyles.dialog}
      contentClassName={alertModalStyles.content}
      backdropClassName={alertModalStyles.backdrop}
      aria-labelledby="intake-form-ai-modal"
    >
      <Modal.Header closeButton className={alertModalStyles.header}>
        <div className={styles.headerContainer}>
          <div className={styles.headerTopRow}>
            <Modal.Title className={`${alertModalStyles.message} ${styles.modalTitle}`}>
              {title}
            </Modal.Title>
          </div>
          <span className={styles.headerSubtitle}>{subtitle}</span>
        </div>
      </Modal.Header>

      <Modal.Body className={`${alertModalStyles.body} ${styles.modalBody}`}>

        {mode === "generate" && (
          <>
            <Row className={styles.formConfigRow}>
              <Col md={4}>
                <TextInput
                  field={{
                    id: "formName",
                    type: "text",
                    label: "Form Name",
                    textStyle: { fontSize: 11 },
                    value: formConfig.formName,
                    show: true,
                    validation: [{ type: "required", message: "Required!" }],
                  }}
                  handleChange={(_id, e) =>
                    setFormConfig((prev) => ({ ...prev, formName: e.target.value }))
                  }
                  handleBlur={() => {}}
                  handleSelect={() => {}}
                />
              </Col>
              <Col md={4}>
                <SelectInput
                  field={{
                    id: "formType",
                    type: "select",
                    label: "Form Type",
                    value: formConfig.formType?.value ?? "",
                    options: FORM_TYPE_OPTIONS,
                    textStyle: { fontSize: 11 },
                    show: true,
                    validation: [{ type: "required", message: "Required!" }],
                  }}
                  handleChange={(_id, val) => {
                    if (val) setFormConfig((prev) => ({ ...prev, formType: val }));
                  }}
                  handleBlur={() => {}}
                  handleSelect={() => {}}
                />
              </Col>
              <Col md={4}>
                <SelectInput
                  field={{
                    id: "lineOfService",
                    type: "select",
                    label: "Line of Service",
                    value: formConfig.lineOfService?.value ?? "",
                    options: lineOfServiceOptions,
                    textStyle: { fontSize: 11 },
                    show: true,
                    validation: [{ type: "required", message: "Required!" }],
                  }}
                  handleChange={(_id, val) => {
                    if (val) setFormConfig((prev) => ({ ...prev, lineOfService: val }));
                  }}
                  handleBlur={() => {}}
                  handleSelect={() => {}}
                />
              </Col>
            </Row>

            <div className={styles.attachmentSection}>
              <label className={styles.attachmentHeaderLabel}>
                <Icon.Paperclip className={styles.attachmentHeaderIcon} />
                Import from existing form
                <span className={styles.instructionsOptional}>(optional)</span>
              </label>
              <span className={styles.attachmentHeaderHint}>
                Upload an image or PDF of your current intake form — AI will build the structure from it
              </span>
              
              {attachment && (
                <>
                  <div className={styles.attachmentPriorityBadge}>
                    <Icon.StarFill className={styles.priorityStarIcon} />
                    Priority source — form structure will be based on this attachment
                  </div>
                </>
              )}
              <DropFileInput
                key={`attachment-upload-${show}`}
                onFileChange={(files: File[]) => {
                  const file = files?.[0] ?? null;
                  if (!file || ACCEPTED_ATTACHMENT_MIME.has(file.type)) {
                    setAttachment(file);
                  }
                }}
                fileLimit="single"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                message="Drag and drop or click to upload — PDF, JPG, PNG, WebP"
              />

              {attachment && (
                <>
                  <div className={styles.attachmentFile}>
                    <Icon.FileEarmarkFill className={styles.attachmentFileIcon} />
                    <span className={styles.attachmentFileName}>{attachment.name}</span>
                    <span className={styles.attachmentFileSize}>
                      ({(attachment.size / 1024).toFixed(1)} KB)
                    </span>
                    <button
                      type="button"
                      className={styles.attachmentRemoveBtn}
                      onClick={() => setAttachment(null)}
                      aria-label="Remove attachment"
                    >
                      <Icon.XCircleFill />
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        <div className={`${styles.instructionsWrapper} ${showFieldPicker === false ? styles.instructionsWrapperFull : ""}`}>
          <label className={styles.instructionsLabel} htmlFor="intakeFormInstructions">
            <Icon.Stars className={styles.instructionsIcon} />
            Instructions
            {mode === "generate" && (
              <span className={styles.instructionsOptional}>(optional)</span>
            )}
          </label>
          <textarea
            id="intakeFormInstructions"
            className={`${styles.instructionsTextarea} ${showFieldPicker === false ? styles.instructionsTextareaFull : ""}`}
            placeholder={
              mode === "modify"
                ? "Describe the changes you'd like — e.g. 'Add a section for emergency contact details, make labels more client-friendly.'"
                : "Describe how you'd like the AI to design this form — e.g. 'Keep it concise, group tax fields together, use friendly labels for clients.'"
            }
            value={formConfig.instructions}
            onChange={(e) =>
              setFormConfig((prev) => ({ ...prev, instructions: e.target.value }))
            }
            rows={3}
            maxLength={2000}
          />
          <span className={styles.instructionsCharCount}>
            {formConfig.instructions.length} / 2000
          </span>
        </div>

        {showFieldPicker !== false && (
          <>
            <div className={styles.toolbarRow}>
              <div className={styles.searchWrapper}>
                <Icon.Search className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search fields..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className={styles.selectAllWrapper}>
                <CheckboxInput
                  field={{
                    id: "selectAll",
                    type: "checkbox",
                    label: "Select All",
                    value: allFilteredSelected,
                    show: true,
                  }}
                  handleChange={(_id, e) => toggleAll(e.target.checked)}
                  handleBlur={() => {}}
                  handleSelect={() => {}}
                />
              </div>
            </div>

            <div className={styles.selectionRow}>
              <div className={styles.selectionBadge}>
                <Icon.CheckCircleFill className={styles.badgeIcon} />
                {selectedIds.size} field{selectedIds.size !== 1 ? "s" : ""} selected
                {attachment && selectedIds.size > 0 && (
                  <span className={styles.selectionExtraHint}> (added on top of attachment)</span>
                )}
              </div>
              {mode !== "modify" && (
                <button
                  type="button"
                  className={"download-links"}
                  onClick={handleSetAsDefault}
                  disabled={isSavingDefault || isLoading || selectedIds.size === 0}
                >
                  Set as default
                </button>
              )}
            </div>

            {isLoading ? (
              <div className={styles.loaderContainer}>
                <div className={styles.spinner} />
                <span className={styles.loaderText}>Loading fields...</span>
              </div>
            ) : filteredFields.length === 0 ? (
              <div className={styles.emptyState}>
                <Icon.InboxFill className={styles.emptyIcon} />
                <span>No fields found</span>
              </div>
            ) : (
              <div className={styles.sectionsContainer}>
                {groupFieldsByCategory(filteredFields).map((section) => {
                  const isExpanded = searchQuery !== "" || !collapsedSections.has(section.categoryKey);
                  const selectedInSection = section.fields.filter((f) => selectedIds.has(f.id)).length;
                  return (
                    <div key={section.categoryKey} className={styles.sectionGroup}>
                      <div
                        className={styles.sectionHeader}
                        onClick={() => toggleSection(section.categoryKey)}
                        role="button"
                      >
                        <Icon.ChevronRight
                          className={`${styles.sectionChevron} ${isExpanded ? styles.sectionChevronOpen : ""}`}
                        />
                        <span className={styles.sectionTitle}>{section.label}</span>
                        <div className={styles.sectionLine} />
                        {!isExpanded && selectedInSection > 0 && (
                          <span className={styles.sectionSelectedPill}>{selectedInSection} selected</span>
                        )}
                        <span className={styles.sectionCount}>{section.fields.length}</span>
                      </div>
                      {isExpanded && (
                        <div className={styles.fieldsGrid}>
                          {section.fields.map((field) => (
                            <div
                              key={field.id}
                              className={`${styles.fieldCard} ${
                                selectedIds.has(field.id) ? styles.fieldCardSelected : ""
                              }`}
                              onClick={() => toggleField(field.id, !selectedIds.has(field.id))}
                            >
                              <div className={styles.fieldCardInner}>
                                <CheckboxInput
                                  field={{
                                    id: `field_${field.id}`,
                                    type: "checkbox",
                                    label: "",
                                    value: selectedIds.has(field.id),
                                    show: true,
                                  }}
                                  handleChange={(_id, e) => {
                                    e.stopPropagation();
                                    toggleField(field.id, e.target.checked);
                                  }}
                                  handleBlur={() => {}}
                                  handleSelect={() => {}}
                                />
                                <div className={styles.fieldInfo}>
                                  <span className={styles.fieldLabel}>{field.label}</span>
                                  <span className={styles.fieldMeta}>
                                    <span
                                      className={`${styles.typeBadge} ${styles[`type_${field.type}`] ?? ""}`}
                                    >
                                      {TYPE_ICON_MAP[field.type] ?? field.type}
                                    </span>
                                    <span className={styles.fieldCode}>{field.code}</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className={alertModalStyles.footer}>
        <CustomButton onclick={onHide} content="Cancel" type="quinary" />
        <CustomButton
          progress={isSubmitting}
          onclick={handleSubmit}
          content={submitLabel}
          disabled={!canSubmit || isSubmitting}
          type="tertiary"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default IntakeFormAIModal;
